import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Form, FormLabel } from "react-bootstrap";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import { ApiError } from "../../../Common/ApiError";
import LoadingCenter from "../../../Common/LoadingCenter";
import InputMoney from "../../../Forms/Element/InputMoney";
import { convertDoubleToStringPTBR } from "../../../../../utils/ConverterUtils";
import { describeDrawerDifference } from "./cashPresentation";
import "./ClosingCashData.css";

// Mesmo limite da coluna CLOSING_OBS (CashService.CLOSING_OBS_MAX_LENGTH).
export const CLOSING_OBS_MAX_LENGTH = 500;

/**
 * Confirmação do fechamento do caixa: resumo do turno, dinheiro esperado na gaveta e os campos
 * opcionais de contagem e observação.
 *
 * O resumo vem do mesmo endpoint do detalhe do caixa (CashService.summarize), para a confirmação e
 * o detalhe mostrarem os mesmos números. A falta ou sobra aparece enquanto o operador digita a
 * contagem, antes de confirmar.
 *
 * @param {{cashId: number, value: {countedCash: string, closingObs: string}, onChange: function}} props
 *        value e onChange ficam com a tela de Caixas, que envia os dois ao confirmar
 */
const ClosingCashData = ({ cashId, value, onChange }) => {

    const { t } = useTranslation();
    const [showLoading, setShowLoading] = useState(false);
    const [summary, setSummary] = useState(null);

    useEffect(() => {
        let active = true;
        setShowLoading(true);
        axiosInstance.get(`${Api.cash_flow.cash}/${cashId}/summary`)
            .then(resp => {
                if (active) setSummary(resp.data);
            })
            .catch(error => {
                if (active) ApiError(error);
            })
            .finally(() => {
                if (active) setShowLoading(false);
            });
        return () => {
            active = false;
        };
    }, [cashId]);

    if (showLoading) {
        return <LoadingCenter />;
    }

    const money = (amount) => `${t('currency_symbol')} ${convertDoubleToStringPTBR(amount || 0)}`;
    const countedCash = value?.countedCash ?? '';
    const closingObs = value?.closingObs ?? '';
    const expected = summary?.expectedDrawerCash;
    const situation = countedCash !== '' && expected != null
        ? describeDrawerDifference(Number(countedCash) - expected)
        : null;

    return (
        <div className="closing-cash">
            <p>{t('cash_flow.message.confirm_cash_register_closure')}</p>
            <ul className="mb-3">
                <li><strong>{t('cash_flow.opening_balance')}:</strong> {money(summary?.openingBalance)}</li>
                <li><strong>{t('cash_flow.incomes')}:</strong> <span className="text-success">{money(summary?.totalIncomes)}</span></li>
                <li><strong>{t('cash_flow.expenses')}:</strong> <span className="text-danger">{money(summary?.totalExpenses)}</span></li>
                <li><strong>{t('cash_flow.final_balance')}:</strong>{' '}
                    <span className={summary?.closingBalance >= 0 ? "text-success" : "text-danger"}>
                        {money(summary?.closingBalance)}
                    </span>
                </li>
            </ul>

            {expected != null && (
                <div className="closing-cash__drawer">
                    <span>{t('cash_flow.expected_drawer_cash')}</span>
                    <strong>{money(expected)}</strong>
                </div>
            )}

            {/* Contagem opcional: sem ela o fechamento funciona como antes. */}
            {expected != null && (
                <div className="mb-3">
                    <FormLabel htmlFor="closing-counted-cash">{t('cash_flow.counted_cash')}</FormLabel>
                    <InputMoney
                        id="closing-counted-cash"
                        currency
                        value={countedCash}
                        onChange={(next, values, sourceInfo) => {
                            // Só a digitação do operador muda a contagem; a formatação da prop não.
                            if (sourceInfo?.source === 'prop') return;
                            onChange({ ...value, countedCash: next });
                        }}
                    />
                    {situation && (
                        <div className={`closing-cash__difference closing-cash__difference--${situation.tone}`} role="status">
                            {t(situation.key, { amount: money(situation.amount) })}
                        </div>
                    )}
                </div>
            )}

            <div className="mb-3">
                <FormLabel htmlFor="closing-obs">{t('cash_flow.closing_obs')}</FormLabel>
                <Form.Control
                    id="closing-obs"
                    as="textarea"
                    rows={5}
                    maxLength={CLOSING_OBS_MAX_LENGTH}
                    placeholder={t('cash_flow.closing_obs_placeholder')}
                    value={closingObs}
                    onChange={(event) => onChange({ ...value, closingObs: event.target.value })}
                />
                <small className="text-muted">{closingObs.length}/{CLOSING_OBS_MAX_LENGTH}</small>
            </div>

            <Alert variant="info" className="mb-0">
                <i className="fa-solid fa-circle-info me-2" aria-hidden="true" />
                Se tudo estiver correto, clique em <strong>Confirmar</strong> para finalizar o fechamento.
            </Alert>
        </div>
    );
}

export default ClosingCashData;
